import { useQuery } from "@tanstack/react-query";

import type { IxdReportSummary } from "@/apis/ixd-editor/types";

import { ExistingReportPicker } from "@/components/common/existing-report-picker";
import { IxdReportName } from "@/components/common/ixd-report-name";
import queries from "@/queries";
import { getFormattedDateTimeKST } from "@/utils/time";

interface ExistingIxdReportPickerProps {
  selectedReportId: string | null;
  /**
   * 고른 항목을 통째로 넘긴다 — 호출부가 파일명·기간을 쓰려고 목록 캐시를 다시
   * 읽지 않아도 되고, "고른 id" 와 "고른 항목" 이 어긋날 일도 없다.
   */
  onSelect: (reportId: string | null, report: IxdReportSummary | null) => void;
  enabled?: boolean;
  title?: string;
  description?: string;
  showSelectionIndicator?: boolean;
  showMetadata?: boolean;
  className?: string;
  listHeight?: "cap" | "fill";
  selectedTone?: "mono" | "blue";
}

export const ExistingIxdReportPicker = ({
  selectedReportId,
  onSelect,
  enabled = true,
  title = "IXD 선택",
  description = "기존 IXD를 사용하여 비교합니다",
  showSelectionIndicator = false,
  showMetadata = true,
  className,
  listHeight,
  selectedTone,
}: ExistingIxdReportPickerProps) => {
  const { data, isLoading, isError, refetch } = useQuery({
    ...queries.ixdEditorReports(),
    enabled,
    staleTime: 60 * 1000,
  });
  const reports = data?.reports ?? [];

  return (
    <ExistingReportPicker
      title={title}
      description={description}
      reports={reports}
      selectedReportId={selectedReportId}
      onSelect={onSelect}
      getReportId={(report) => report.reportId}
      showSelectionIndicator={showSelectionIndicator}
      renderReportTitle={(report) => (
        <IxdReportName
          fileName={report.originalFilename}
          periodYear={report.periodYear}
          periodType={report.periodType}
        />
      )}
      renderReportMeta={
        showMetadata
          ? (report) => getFormattedDateTimeKST(report.uploadedAt)
          : undefined
      }
      isLoading={isLoading}
      isError={isError}
      emptyMessage="업로드된 IXD가 없습니다."
      errorMessage="업로드한 IXD 파일 목록을 불러오는 중 오류가 발생했습니다."
      onRetry={() => refetch()}
      className={className}
      listHeight={listHeight}
      selectedTone={selectedTone}
    />
  );
};
